import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type { GetPublicWhiteLabelResponse } from '@/api/v1/public/white-label'
import { LOCAL_STORAGE_KEYS } from '@/lib/local-storage-keys'
import {
  applyWhiteLabelTheme,
  updateFavicon,
  updateManifest,
} from '@/lib/white-label'

interface WhiteLabelStore extends GetPublicWhiteLabelResponse {
  isLoading: boolean
  lastFetched: number | null
  setWhiteLabel: (data: GetPublicWhiteLabelResponse) => void
  setLoading: (loading: boolean) => void
  clearWhiteLabel: () => void
  shouldRefetch: () => boolean
}

const defaultState: GetPublicWhiteLabelResponse = {
  isWhiteLabel: false,
  companyName: 'Signater',
  theme: null,
  rectangleLightLogoUrl: null,
  rectangleDarkLogoUrl: null,
  squareLightLogoUrl: null,
  squareDarkLogoUrl: null,
  faviconUrl: null,
  googleAppClientId: null,
  microsoftAppClientId: null,
  turnstileSiteKey: null,
  hasSupportChatDisabled: false,
  isSsoAvailable: false,
}

const CACHE_DURATION = 1000 * 60 // 1 minute

export const useWhiteLabelStore = create<WhiteLabelStore>()(
  // @ts-ignore - Known issue with Zustand 5.x and TypeScript strict mode with persist middleware
  persist(
    (set, get) => ({
      ...defaultState,
      isLoading: true,
      lastFetched: null,

      setWhiteLabel: (data) =>
        set({
          ...data,
          isLoading: false,
          lastFetched: Date.now(),
        }),

      setLoading: (loading) => set({ isLoading: loading }),

      clearWhiteLabel: () =>
        set({
          ...defaultState,
          isLoading: false,
          lastFetched: null,
        }),

      shouldRefetch: () => {
        const { lastFetched } = get()
        if (!lastFetched) return true
        return Date.now() - lastFetched > CACHE_DURATION
      },
    }),
    {
      name: LOCAL_STORAGE_KEYS.WHITE_LABEL,
      // Apply white-label after rehydration (title is managed by usePageTitle hook)
      onRehydrateStorage: () => (state) => {
        if (state && state.isWhiteLabel) {
          applyWhiteLabelTheme(state.theme)
          updateFavicon(state.faviconUrl)
          updateManifest(state.companyName, state.faviconUrl)
        }
      },
    },
  ),
)
